import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserPlus, 
  faUsers,
  faUserEdit, 
  faTrash, 
  faSave, 
  faTimes, 
  faFileAlt,
  faSpinner,
  faSearch,
  faFilter,
  faCheckSquare,
  faSquare,
  faUserCog,
  faBuilding,
  faEnvelope,
  faKey,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/components/users/UserManagement.css';
import firebaseUtils from '../../utils/firebase-utils-simple';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showCertModal, setShowCertModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    nombre: '',
    empresa: '',
    rol: 'cliente'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('nombre');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddForm, setShowAddForm] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Iniciando carga de datos...');

      // Cargar certificados con fallback por permisos
      let certificadosData = [];
      try {
        const certificadosSnapshot = await getDocs(collection(db, 'certificados'));
        certificadosData = certificadosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          codigoUnico: doc.data().codigoUnico || 'Sin código',
          tipoProducto: doc.data().tipoProducto || 'Sin tipo',
          fechaGeneracion: doc.data().fechaGeneracion || '',
          creadoPor: doc.data().creadoPor || ''
        }));
      } catch (errCert) {
        console.warn('⚠️ Consulta directa certificados falló, usando fallback:', errCert.message);
        certificadosData = await firebaseUtils.getCertificados({ forceRefresh: false });
      }
      setCertificates(certificadosData);

      // Cargar usuarios con fallback por permisos
      let usersData = [];
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          assignedPdfs: doc.data().assignedPdfs || [],
          createdAt: doc.data().createdAt || new Date().toISOString()
        }));
      } catch (errUsers) {
        console.warn('⚠️ Consulta directa usuarios falló, usando fallback:', errUsers.message);
        usersData = await firebaseUtils.getUsuarios();
      }
      setUsers(usersData);

    } catch (error) {
      console.error('Error al cargar datos:', error);
      showNotification('Error al cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );

      // Agregar a Firestore
      await addDoc(collection(db, 'users'), {
        uid: userCredential.user.uid,
        email: newUser.email,
        nombre: newUser.nombre,
        empresa: newUser.empresa,
        rol: newUser.rol,
        assignedPdfs: [],
        createdAt: new Date().toISOString()
      });

      // Reset form
      setNewUser({
        email: '',
        password: '',
        nombre: '',
        empresa: '',
        rol: 'cliente'
      });
      setShowAddForm(false);
      fetchData();
      showNotification('Usuario creado exitosamente');
    } catch (error) {
      console.error('Error al crear usuario:', error);
      showNotification('Error al crear usuario: ' + error.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updatedData) => {
    setActionLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, updatedData);
      setEditingUser(null);
      fetchData();
      showNotification('Usuario actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      showNotification('Error al actualizar usuario', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      setActionLoading(true);
      try {
        await deleteDoc(doc(db, 'users', userId));
        fetchData();
        showNotification('Usuario eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
        showNotification('Error al eliminar usuario', 'error');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleBulkAssignCertificates = async () => {
    if (selectedUsers.length === 0 || selectedCertificates.length === 0) {
      showNotification('Selecciona usuarios y certificados para asignar', 'error');
      return;
    }

    setActionLoading(true);
    try {
      const batch = writeBatch(db);
      
      selectedUsers.forEach(userId => {
        const userRef = doc(db, 'users', userId);
        const user = users.find(u => u.id === userId);
        const existingCerts = user.assignedPdfs || [];
        const newCerts = [...new Set([...existingCerts, ...selectedCertificates])];
        
        batch.update(userRef, { assignedPdfs: newCerts });
      });

      await batch.commit();
      fetchData();
      setSelectedUsers([]);
      setSelectedCertificates([]);
      setBulkSelectMode(false);
      showNotification('Certificados asignados exitosamente');
    } catch (error) {
      console.error('Error en asignación masiva:', error);
      showNotification('Error al asignar certificados', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignCertificates = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        assignedPdfs: selectedCertificates
      });

      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? {...user, assignedPdfs: selectedCertificates}
          : user
      ));

      setShowCertModal(false);
      setSelectedUser(null);
      setSelectedCertificates([]);
      showNotification('Certificados asignados exitosamente');
    } catch (error) {
      console.error('Error al asignar certificados:', error);
      showNotification('Error al asignar certificados', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Filtros y ordenamiento
  const getFilteredAndSortedUsers = () => {
    let filtered = users.filter(user => {
      const matchesSearch = 
        user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.empresa.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || user.rol === roleFilter;
      const matchesCompany = companyFilter === 'all' || user.empresa === companyFilter;
      
      return matchesSearch && matchesRole && matchesCompany;
    });

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'assignedPdfs') {
        aValue = (a.assignedPdfs || []).length;
        bValue = (b.assignedPdfs || []).length;
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const getUniqueCompanies = () => {
    return [...new Set(users.map(user => user.empresa))].filter(Boolean);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Cargando gestión de usuarios...</p>
      </div>
    );
  }

  const filteredUsers = getFilteredAndSortedUsers();
  const companies = getUniqueCompanies();

  return (
    <div className="user-management-modern">
      {/* Header */}
      <div className="management-header">
        <div className="header-content">
          <div className="header-title">
            <FontAwesomeIcon icon={faUsers} />
            <h1>Gestión de Usuarios</h1>
            <span className="user-count">{users.length} usuarios registrados</span>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              Nuevo Usuario
            </button>
            
            <button 
              className={`btn-secondary ${bulkSelectMode ? 'active' : ''}`}
              onClick={() => setBulkSelectMode(!bulkSelectMode)}
            >
              <FontAwesomeIcon icon={faCheckSquare} />
              Selección Múltiple
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {bulkSelectMode && (
          <div className="bulk-actions-bar">
            <div className="bulk-info">
              <span>{selectedUsers.length} usuarios seleccionados</span>
            </div>
            <div className="bulk-actions">
              <button 
                className="btn-bulk"
                onClick={() => setShowCertModal(true)}
                disabled={selectedUsers.length === 0}
              >
                <FontAwesomeIcon icon={faFileAlt} />
                Asignar Certificados
              </button>
              <button 
                className="btn-cancel"
                onClick={() => {
                  setBulkSelectMode(false);
                  setSelectedUsers([]);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="search-filters-section">
        <div className="search-container">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre, email o empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <button 
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
          Filtros
        </button>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Rol</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">Todos los roles</option>
                <option value="admin">Administradores</option>
                <option value="cliente">Clientes</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Empresa</label>
              <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
                <option value="all">Todas las empresas</option>
                {companies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Ordenar por</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="nombre">Nombre</option>
                <option value="email">Email</option>
                <option value="empresa">Empresa</option>
                <option value="rol">Rol</option>
                <option value="assignedPdfs">Certificados</option>
                <option value="createdAt">Fecha de creación</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Orden</label>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="asc">Ascendente</option>
                <option value="desc">Descendente</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Users Grid */}
      <div className="users-grid-modern">
        {filteredUsers.map(user => (
          <UserCard 
            key={user.id}
            user={user}
            certificates={certificates}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            onUpdate={handleUpdateUser}
            onDelete={handleDeleteUser}
            onAssignCertificates={() => {
              setSelectedUser(user);
              setSelectedCertificates(user.assignedPdfs || []);
              setShowCertModal(true);
            }}
            bulkSelectMode={bulkSelectMode}
            isSelected={selectedUsers.includes(user.id)}
            onToggleSelect={(userId) => {
              if (selectedUsers.includes(userId)) {
                setSelectedUsers(selectedUsers.filter(id => id !== userId));
              } else {
                setSelectedUsers([...selectedUsers, userId]);
              }
            }}
            actionLoading={actionLoading}
          />
        ))}
        
        {filteredUsers.length === 0 && (
          <div className="empty-state">
            <FontAwesomeIcon icon={faUsers} size="3x" />
            <h3>No se encontraron usuarios</h3>
            <p>Intenta ajustar los filtros de búsqueda</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddForm && (
        <AddUserModal 
          newUser={newUser}
          setNewUser={setNewUser}
          onSubmit={handleAddUser}
          onClose={() => setShowAddForm(false)}
          loading={actionLoading}
        />
      )}

      {/* Certificate Assignment Modal */}
      {showCertModal && (
        <CertificateModal 
          certificates={certificates}
          selectedCertificates={selectedCertificates}
          setSelectedCertificates={setSelectedCertificates}
          onAssign={bulkSelectMode ? handleBulkAssignCertificates : handleAssignCertificates}
          onClose={() => {
            setShowCertModal(false);
            setSelectedUser(null);
            setSelectedCertificates([]);
          }}
          loading={actionLoading}
          title={
            bulkSelectMode 
              ? `Asignar certificados a ${selectedUsers.length} usuarios`
              : `Asignar certificados a ${selectedUser?.nombre}`
          }
        />
      )}

      {/* Notificación */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon 
            icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} 
          />
          {notification.message}
        </div>
      )}
    </div>
  );
}

// Componente UserCard
const UserCard = ({ 
  user, 
  certificates, 
  editingUser, 
  setEditingUser, 
  onUpdate, 
  onDelete, 
  onAssignCertificates,
  bulkSelectMode,
  isSelected,
  onToggleSelect,
  actionLoading
}) => {
  const [formData, setFormData] = useState({});

  const handleEditStart = () => {
    setFormData({
      nombre: user.nombre,
      empresa: user.empresa,
      rol: user.rol
    });
    setEditingUser(user.id);
  };

  const handleEditSave = () => {
    onUpdate(user.id, formData);
  };

  const assignedCertificates = certificates.filter((cert) =>
    user.assignedPdfs?.includes(cert.id)
  );

  return (
    <div className={`user-card-modern ${isSelected ? 'selected' : ''}`}>
      {bulkSelectMode && (
        <div className="bulk-select-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(user.id)}
          />
        </div>
      )}

      <div className="user-card-header">
        <div className="user-info">
          <h3>{user.nombre}</h3>
          <span className={`role-badge ${user.rol}`}>
            <FontAwesomeIcon icon={user.rol === 'admin' ? faUserCog : faUsers} />
            {user.rol}
          </span>
        </div>
        <div className="user-stats">
          <span className="cert-count">
            {assignedCertificates.length} certificados
          </span>
        </div>
      </div>

      <div className="user-card-body">
        {editingUser === user.id ? (
          <div className="edit-form">
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Empresa</label>
              <input
                type="text"
                value={formData.empresa}
                onChange={(e) => setFormData({...formData, empresa: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Rol</label>
              <select
                value={formData.rol}
                onChange={(e) => setFormData({...formData, rol: e.target.value})}
              >
                <option value="cliente">Cliente</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="user-details">
            <div className="detail-item">
              <FontAwesomeIcon icon={faEnvelope} />
              <span>{user.email}</span>
            </div>
            <div className="detail-item">
              <FontAwesomeIcon icon={faBuilding} />
              <span>{user.empresa}</span>
            </div>
            {assignedCertificates.length > 0 && (
              <div className="assigned-certificates">
                <h5>Certificados asignados:</h5>
                <div className="cert-list">
                  {assignedCertificates.slice(0, 3).map(cert => (
                    <div key={cert.id} className="cert-tag">
                      {cert.codigoUnico}
                    </div>
                  ))}
                  {assignedCertificates.length > 3 && (
                    <span className="cert-more">+{assignedCertificates.length - 3} más</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="user-card-actions">
        {editingUser === user.id ? (
          <div className="edit-actions">
            <button 
              onClick={handleEditSave}
              className="btn-save"
              disabled={actionLoading}
            >
              <FontAwesomeIcon icon={faSave} />
              Guardar
            </button>
            <button 
              onClick={() => setEditingUser(null)}
              className="btn-cancel"
            >
              <FontAwesomeIcon icon={faTimes} />
              Cancelar
            </button>
          </div>
        ) : (
          <div className="normal-actions">
            <button 
              onClick={onAssignCertificates}
              className="btn-assign"
            >
              <FontAwesomeIcon icon={faFileAlt} />
              Certificados
            </button>
            <button 
              onClick={handleEditStart}
              className="btn-edit"
            >
              <FontAwesomeIcon icon={faUserEdit} />
              Editar
            </button>
            <button 
              onClick={() => onDelete(user.id)}
              className="btn-delete"
              disabled={actionLoading}
            >
              <FontAwesomeIcon icon={faTrash} />
              Eliminar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente AddUserModal
const AddUserModal = ({ newUser, setNewUser, onSubmit, onClose, loading }) => (
  <div className="modal-overlay">
    <div className="modal-content-modern">
      <div className="modal-header">
        <h3>
          <FontAwesomeIcon icon={faUserPlus} />
          Agregar Nuevo Usuario
        </h3>
        <button onClick={onClose} className="modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="add-user-form-modern">
        <div className="form-grid">
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faUsers} />
              Nombre completo
            </label>
            <input
              type="text"
              value={newUser.nombre}
              onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
              required
              placeholder="Nombre y apellido"
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faEnvelope} />
              Email
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              required
              placeholder="email@empresa.com"
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faKey} />
              Contraseña
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              required
              placeholder="Mínimo 6 caracteres"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faBuilding} />
              Empresa
            </label>
            <input
              type="text"
              value={newUser.empresa}
              onChange={(e) => setNewUser({...newUser, empresa: e.target.value})}
              required
              placeholder="Nombre de la empresa"
            />
          </div>

          <div className="form-group full-width">
            <label>
              <FontAwesomeIcon icon={faUserCog} />
              Rol
            </label>
            <select
              value={newUser.rol}
              onChange={(e) => setNewUser({...newUser, rol: e.target.value})}
              required
            >
              <option value="cliente">Cliente</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
        </div>

        <div className="modal-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Creando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserPlus} />
                Crear Usuario
              </>
            )}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  </div>
);

// Componente CertificateModal
const CertificateModal = ({ 
  certificates, 
  selectedCertificates, 
  setSelectedCertificates, 
  onAssign, 
  onClose, 
  loading, 
  title 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  const normalizedSelected = [...new Set(selectedCertificates)];
  const selectedCount = certificates.filter(cert => normalizedSelected.includes(cert.id)).length;

  const filteredCertificates = certificates.filter(cert =>
    cert.codigoUnico.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.tipoProducto.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCertificates([]);
    } else {
      setSelectedCertificates(filteredCertificates.map(cert => cert.id));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleCertificate = (certId) => {
    if (selectedCertificates.includes(certId)) {
      setSelectedCertificates(selectedCertificates.filter(id => id !== certId));
    } else {
      setSelectedCertificates([...selectedCertificates, certId]);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-large">
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faFileAlt} />
            {title}
          </h3>
          <button onClick={onClose} className="modal-close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="certificate-search">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar certificados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button onClick={handleSelectAll} className="select-all-btn">
              <FontAwesomeIcon icon={selectAll ? faSquare : faCheckSquare} />
              {selectAll ? 'Deseleccionar todo' : 'Seleccionar todo'}
            </button>
          </div>

          <div className="certificates-list">
            {filteredCertificates.length === 0 ? (
              <div className="no-certificates">
                <FontAwesomeIcon icon={faFileAlt} size="2x" />
                <p>No hay certificados disponibles</p>
              </div>
            ) : (
              filteredCertificates.map(cert => (
                <div 
                  key={cert.id} 
                  className={`certificate-item ${selectedCertificates.includes(cert.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleCertificate(cert.id)}
                >
                  <div className="cert-checkbox">
                    <FontAwesomeIcon 
                      icon={selectedCertificates.includes(cert.id) ? faCheckSquare : faSquare} 
                    />
                  </div>
                  <div className="cert-info">
                    <div className="cert-title">
                      <strong>{cert.codigoUnico}</strong>
                      <span className="cert-type">{cert.tipoProducto}</span>
                    </div>
                    <div className="cert-details">
                      {cert.fechaGeneracion && (
                        <span>
                          Generado: {new Date(cert.fechaGeneracion).toLocaleDateString()}
                        </span>
                      )}
                      {cert.creadoPor && (
                        <span>Por: {cert.creadoPor}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="modal-actions">
          <div className="selection-count">
            {selectedCount} certificados seleccionados
          </div>
          <button 
            onClick={onAssign} 
            className="btn-primary"
            disabled={loading || selectedCertificates.length === 0}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Asignando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileAlt} />
                Asignar Certificados
              </>
            )}
          </button>
          <button onClick={onClose} className="btn-secondary">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;